import { useState, useEffect } from 'react';

interface UseActionIndicatorProps {
  isVerified: boolean | null;
  requiresPasswordReset: boolean | null;
}

interface UseActionIndicatorReturn {
  hasPendingActions: boolean;
  pendingActionCount: number;
  needsEmailVerification: boolean;
  needsPasswordReset: boolean;
  isExpanded: boolean;
  isDismissed: boolean;
  shouldAutoExpand: boolean;
  emailModalOpen: boolean;
  passwordModalOpen: boolean;
  togglePanel: () => void;
  dismissIndicator: () => void;
  openEmailModal: () => void;
  closeEmailModal: () => void;
  openPasswordModal: () => void;
  closePasswordModal: () => void;
}

const STORAGE_KEY_DISMISSED = 'eip_indicator_dismissed';

/**
 * Custom hook for managing ActionIndicator state
 * Handles:
 * - Pending actions detection (email verification, password reset)
 * - First-time auto-expand logic (localStorage)
 * - Dismiss state (sessionStorage)
 * - Modal open/close state
 */
export function useActionIndicator({ isVerified, requiresPasswordReset }: UseActionIndicatorProps): UseActionIndicatorReturn {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const needsEmailVerification = isVerified === false;
  const needsPasswordReset = requiresPasswordReset === true;
  const hasPendingActions = needsEmailVerification || needsPasswordReset;
  const pendingActionCount = (needsEmailVerification ? 1 : 0) + (needsPasswordReset ? 1 : 0);

  // Always auto-expand when there are pending actions (removed localStorage check)
  const shouldAutoExpand = hasPendingActions;

  // Check if indicator is dismissed for current session
  // Clear dismissed state on mount (fresh login) so indicator reappears
  useEffect(() => {
    // On component mount (new login), clear any previous dismissals
    // This ensures indicator reappears each time user logs in
    sessionStorage.removeItem(STORAGE_KEY_DISMISSED);
    setIsDismissed(false);
  }, []);

  // Auto-expand immediately when there are pending actions
  useEffect(() => {
    if (shouldAutoExpand && !isDismissed) {
      setIsExpanded(true);
    }
  }, [shouldAutoExpand, isDismissed]);

  // Reset dismissed state when actions are completed
  useEffect(() => {
    if (!hasPendingActions) {
      sessionStorage.removeItem(STORAGE_KEY_DISMISSED);
      setIsDismissed(false);
      setIsExpanded(false);
    }
  }, [hasPendingActions]);

  const togglePanel = () => {
    setIsExpanded((prev) => !prev);
  };

  const dismissIndicator = () => {
    sessionStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
    setIsDismissed(true);
    setIsExpanded(false);
  };

  const openEmailModal = () => {
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
  };

  const openPasswordModal = () => {
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
  };

  return {
    hasPendingActions,
    pendingActionCount,
    needsEmailVerification,
    needsPasswordReset,
    isExpanded,
    isDismissed,
    shouldAutoExpand,
    emailModalOpen,
    passwordModalOpen,
    togglePanel,
    dismissIndicator,
    openEmailModal,
    closeEmailModal,
    openPasswordModal,
    closePasswordModal,
  };
}
